import { get } from 'lodash';
import { Action, Dispatch, Middleware, MiddlewareAPI, Reducer } from '@reduxjs/toolkit';

import { ReduxAction, ReduxState } from 'app/redux/types';

import {
  getClientInfo,
} from 'app/lib/account/actions';
import {
  clientId as selectClientId,
} from 'app/lib/account/selectors';

import { logout } from './actions/auth';
import { BACKUP, RESTORE } from './actions/persistence';
import persistentNodes from './reducers/persistent';

// ---

function createDeepKeysFromPersistentNodes(nodes: typeof persistentNodes): string[] {
  let keys: string[] = [];

  for (const key in nodes) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const node = nodes[key];
    if (nodes.hasOwnProperty(key) && typeof node === 'object') {
      const subKeys = createDeepKeysFromPersistentNodes(node);

      const keysAcrossPoint = subKeys.map((subKey) => `${key}.${subKey}`);

      keys = keys.concat(keysAcrossPoint);
    }
    else {
      keys.push(key);
    }
  }

  return keys;
}

function deepUpdateState<T>(
  state: T,
  newValue: unknown,
  path: string,
): T {
  let key: keyof T;
  const pointIndex = path.indexOf('.');

  if (pointIndex === -1) {
    key = path as keyof T;
  }
  else {
    key = path.substring(0, pointIndex) as keyof T;

    const pathResidue = path.substring(pointIndex + 1, path.length);

    if (pathResidue) {
      return {
        ...state,
        [key]: deepUpdateState(state[key], newValue, pathResidue),
      };
    }
  }

  return {
    ...state,
    [key]: newValue,
  };
}

const persistentNodeNames = createDeepKeysFromPersistentNodes(persistentNodes);

/**
 * Compose a local storage key from `nodeKey` and `clientId` to support multiuser agents.
 */
const composeKey = (nodeKey: string, clientId: string): string => {
  return clientId ? `${nodeKey}:${clientId}` : nodeKey;
};

/**
 * Backup changed persistent nodes.
 */
function backup(prevState: ReduxState, nextState: ReduxState): void {
  // todo: debounce...
  const action = { type: BACKUP };

  try {
    const clientId = selectClientId(nextState);

    if (!clientId) {
      return;
    }

    persistentNodeNames.forEach((key) => {
      const prevStateValue = get(prevState, key);
      const nextStateValue = get(nextState, key);

      if (prevStateValue !== nextStateValue) {
        let data;

        try {
          const reducer = get(persistentNodes, key);
          data = JSON.stringify(reducer(nextStateValue, action));
        }
        catch (err) {
          console.error('unable to persist [%s] state:', key, err); // >>>
          return;
        }

        const storageKey = composeKey(key, clientId);

        if (data) {
          localStorage.setItem(storageKey, data);
        }
        else {
          localStorage.removeItem(storageKey);
        }
      }
    });
  }
  catch (err) {
    console.error('unable to persist redux state:', err); // >>>
  }
}

/**
 * Tries to dispatch the "restore persistent nodes' state" action if any node's state is stored for a current client id.
 */
export function restore(store: MiddlewareAPI<Dispatch, ReduxState>): void {
  const state = store.getState();
  const clientId = selectClientId(state);

  try {
    const payload: Record<string, unknown> = {};

    persistentNodeNames.forEach((key) => {
      const data = clientId ? localStorage.getItem(composeKey(key, clientId)) : null;
      if (data !== null) {
        payload[key] = JSON.parse(data);
      }
    });

    if (Object.keys(payload).length) {
      store.dispatch({
        type: RESTORE,
        payload,
      });
    }
  }
  catch (err) {
    console.error('unable to restore redux state:', err); // >>>
  }
}

function restoreNodeState(
  key: string,
  state: ReduxState,
  payload: Record<string, unknown>,
): ReduxState {
  const reducer = get(persistentNodes, key);
  const nodeState = get(state, key);
  const nodePayload = payload[key];

  const value = reducer(nodeState, {
    type: RESTORE,
    payload: nodePayload,
  });

  return deepUpdateState<ReduxState>(state, value, key);
}

type ConnectPersistenceActions = ReduxAction<typeof RESTORE, Record<string, unknown>>;

/**
 * Wrap the root reducer to intercept and handle the `RESTORE` action manually.
 */
export const connectPersistence = (rootReducer: Reducer<ReduxState>) => {
  return (state: ReduxState, action: ConnectPersistenceActions): ReduxState => {
    if (action.type === RESTORE) {
      return Object.keys(action.payload).reduce((state, key) => {
        return restoreNodeState(key, state, action.payload);
      }, Object.assign({}, state));
    }

    return rootReducer(state, action);
  };
};

/**
 * Redux store state persistence middleware.
 * Backups persistent nodes' state in a local storage when changed.
 */
export const middleware = (): Middleware<unknown, ReduxState> => {
  return (store) => (next) => (action: Action) => {
    if (action.type === RESTORE) { // skip saving when the "restore" changes the state
      return next(action);
    }
    if (action.type === getClientInfo.fulfilled.type || action.type === logout.fulfilled.type) {
      const possiblePromise = next(action);
      restore(store);
      return possiblePromise;
    }

    const prevState = store.getState();
    const possiblePromise = next(action);

    const nextState = store.getState();

    if (prevState !== nextState) {
      backup(prevState, nextState);
    }

    return possiblePromise;
  };
};
